import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import aiAutomationApi from '../utils/aiAutomationApi';
import { getMetadata } from '../utils/encryptedStorage';
import { isMailProviderAuthError } from '../utils/gmailOAuth';

/**
 * SyncContext — Global sync state with SSE-based real-time progress.
 *
 * Tracks active syncs per search_id. Uses SSE (EventSource) for live progress
 * instead of polling. Persists active syncs in localStorage for page refresh recovery.
 *
 * Each sync emits structured events: phase, logs[], resume parsing status.
 */
const SyncContext = createContext(null);

export const useSyncContext = () => {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncContext must be used within SyncProvider');
  return ctx;
};

// AI Automation SSE URL
const AI_SSE_URL = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://127.0.0.1:8100/aibot/v1';

// ── localStorage helpers ──
const ACTIVE_SYNCS_KEY = 'active_syncs';
const SYNC_HISTORY_KEY = 'sync_history';

const getStoredActiveSyncs = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_SYNCS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const storeActiveSync = (searchId, syncId, jobTitle) => {
  try {
    const current = getStoredActiveSyncs();
    current[searchId] = { syncId, jobTitle: jobTitle || '', startedAt: Date.now() };
    localStorage.setItem(ACTIVE_SYNCS_KEY, JSON.stringify(current));
  } catch (_) {}
};

const removeStoredSync = (searchId) => {
  try {
    const current = getStoredActiveSyncs();
    delete current[searchId];
    localStorage.setItem(ACTIVE_SYNCS_KEY, JSON.stringify(current));
  } catch (_) {}
};

const getStoredSyncHistory = () => {
  try {
    const raw = localStorage.getItem(SYNC_HISTORY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const activeSyncs = getStoredActiveSyncs();
    return Object.entries(parsed || {}).reduce((acc, [searchId, state]) => {
      if (!searchId || !state) return acc;
      acc[searchId] = {
        ...state,
        syncing: activeSyncs[searchId] ? Boolean(state.syncing) : false,
      };
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const persistSyncHistory = (syncStates = {}) => {
  try {
    const normalized = Object.entries(syncStates || {}).reduce((acc, [searchId, state]) => {
      if (!searchId || !state) return acc;
      const logs = Array.isArray(state.logs) ? state.logs.slice(-200) : [];
      acc[searchId] = {
        syncing: Boolean(state.syncing),
        phase: state.phase || '',
        logs,
        progress: state.progress || (logs.length > 0 ? String(logs[logs.length - 1] || '').trim() : ''),
        startedAt: state.startedAt || null,
        updatedAt: state.updatedAt || null,
        completedAt: state.completedAt || null,
        resumeParsing: Boolean(state.resumeParsing),
        resumeTotal: state.resumeTotal || 0,
        resumeParsed: state.resumeParsed || 0,
        resumeFailed: state.resumeFailed || 0,
        resumeQueued: state.resumeQueued || 0,
        syncResult: state.syncResult || null,
        jobTitle: state.jobTitle || '',
        senderEmail: state.senderEmail || '',
        lastSync: state.lastSync || null,
      };
      return acc;
    }, {});
    localStorage.setItem(SYNC_HISTORY_KEY, JSON.stringify(normalized));
  } catch (_) {}
};


export const SyncProvider = ({ children }) => {
  // Map of searchId → { syncing, phase, logs[], progress, startedAt, updatedAt, completedAt, resumeParsing, resumeTotal, resumeParsed, resumeFailed, resumeQueued, syncResult, jobTitle }
  const [syncStates, setSyncStates] = useState(() => getStoredSyncHistory());
  // The search_id of the chat the user is currently viewing (set by ChatPage)
  const [currentSearchId, setCurrentSearchId] = useState(null);
  const eventSourcesRef = useRef({}); // searchId → EventSource
  const callbacksRef = useRef({});    // searchId → { onComplete, onFail }
  const mountedRef = useRef(true);

  const getProgressFromLogs = useCallback((logs = []) => {
    if (!Array.isArray(logs) || logs.length === 0) return '';
    return String(logs[logs.length - 1] || '').trim();
  }, []);

  const getSyncState = useCallback((searchId) => {
    const fallback = {
      syncing: false,
      phase: '',
      logs: [],
      progress: '',
      startedAt: null,
      updatedAt: null,
      completedAt: null,
      resumeParsing: false,
      resumeTotal: 0,
      resumeParsed: 0,
      resumeFailed: 0,
      resumeQueued: 0,
      syncResult: null,
      jobTitle: '',
      senderEmail: '',
      lastSync: null,
    };
    if (!searchId) return fallback;
    const state = syncStates[searchId];
    if (!state) return fallback;
    return {
      ...fallback,
      ...state,
      progress: state.progress || getProgressFromLogs(state.logs),
    };
  }, [getProgressFromLogs, syncStates]);

  const isAnySyncing = useCallback(() => {
    return Object.values(syncStates).some((s) => s.syncing);
  }, [syncStates]);

  // Get all active syncs (for the floating bubble summary)
  const getActiveSyncs = useCallback(() => {
    return Object.entries(syncStates)
      .filter(([, s]) => s.syncing)
      .map(([searchId, s]) => ({
        searchId,
        jobTitle: s.jobTitle || searchId,
        phase: s.phase,
        progress: s.logs?.[s.logs.length - 1] || '',
        updatedAt: s.updatedAt || null,
      }));
  }, [syncStates]);

  const updateSyncState = useCallback((searchId, updates) => {
    const nextUpdatedAt = updates?.updatedAt === undefined ? Date.now() : updates.updatedAt;
    setSyncStates((prev) => ({
      ...prev,
      [searchId]: {
        ...(prev[searchId] || { syncing: false, phase: '', logs: [] }),
        ...updates,
        updatedAt: nextUpdatedAt,
      },
    }));
  }, []);

  useEffect(() => {
    persistSyncHistory(syncStates);
  }, [syncStates]);

  // Close SSE for a specific search
  const _closeSSE = useCallback((searchId) => {
    if (eventSourcesRef.current[searchId]) {
      eventSourcesRef.current[searchId].close();
      delete eventSourcesRef.current[searchId];
    }
  }, []);

  // Connect SSE for a sync
  const _connectSSE = useCallback((searchId, syncId, callbackOptions = {}) => {
    // Close existing connection if any
    _closeSSE(searchId);

    if (callbackOptions.onComplete || callbackOptions.onFail) {
      callbacksRef.current[searchId] = callbackOptions;
    }

    const url = `${AI_SSE_URL}/email/sync-stream/${syncId}`;
    const es = new EventSource(url);
    eventSourcesRef.current[searchId] = es;

    es.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(event.data);

        if (data.status === 'unknown') return; // Sync not found yet, keep waiting

        // Accumulate logs (SSE sends only new_logs since last event)
        const newLogs = data.new_logs || [];

        setSyncStates((prev) => {
          const existing = prev[searchId] || { logs: [] };
          const allLogs = [...(existing.logs || []), ...newLogs];
          const nextUpdatedAt = Date.now();
          return {
            ...prev,
            [searchId]: {
              ...existing,
              syncing: true,
              phase: data.phase || 'syncing',
              logs: allLogs,
              progress: getProgressFromLogs(allLogs),
              startedAt: existing.startedAt || nextUpdatedAt,
              updatedAt: nextUpdatedAt,
              completedAt: null,
              resumeParsing: data.resume_parsing || false,
              resumeTotal: data.resume_total || 0,
              resumeParsed: data.resume_parsed || 0,
              resumeFailed: data.resume_failed || 0,
              resumeQueued: data.resume_queued || 0,
              jobTitle: data.job_title || existing.jobTitle || '',
            },
          };
        });
      } catch (e) {
        console.error('[SyncContext] SSE parse error:', e);
      }
    };

    es.addEventListener('complete', (event) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(event.data);
        const newLogs = data.new_logs || [];

        setSyncStates((prev) => {
          const existing = prev[searchId] || { logs: [] };
          const allLogs = [...(existing.logs || []), ...newLogs, 'Sync complete'];
          const completedAt = Date.now();
          return {
            ...prev,
            [searchId]: {
              ...existing,
              syncing: false,
              phase: 'complete',
              syncMessage: '',
              logs: allLogs,
              progress: getProgressFromLogs(allLogs),
              startedAt: existing.startedAt || completedAt,
              updatedAt: completedAt,
              completedAt,
              syncResult: data.result || null,
              senderEmail: data.sender_email || data.result?.sender_email || existing.senderEmail || '',
              lastSync: data.result?.last_synced_at || completedAt,
              resumeParsing: false,
            },
          };
        });
      } catch (_) {}

      _closeSSE(searchId);
      removeStoredSync(searchId);
      const cbs = callbacksRef.current[searchId];
      if (cbs?.onComplete) cbs.onComplete();
      delete callbacksRef.current[searchId];
    });

    es.addEventListener('error', (event) => {
      // SSE 'error' event can be either a named event from server or a connection error
      if (event.data) {
        // Server-sent error event
        let errorPayload = null;
        try {
          const data = JSON.parse(event.data);
          errorPayload = data;
          setSyncStates((prev) => {
            const existing = prev[searchId] || { logs: [] };
            const mailAuthNeeded = Boolean(data.gmail_auth_needed || data.mail_auth_needed || isMailProviderAuthError(data));
            const authMessage = 'Mail authorization required. Waiting for reconnect...';
            const allLogs = [
              ...(existing.logs || []),
              mailAuthNeeded ? authMessage : `Sync failed: ${data.error || 'Unknown error'}`,
            ];
            const failedAt = Date.now();
            return {
              ...prev,
              [searchId]: {
                ...existing,
                syncing: false,
                phase: mailAuthNeeded ? 'auth_required' : 'error',
                syncMessage: '',
                logs: allLogs,
                progress: mailAuthNeeded ? authMessage : getProgressFromLogs(allLogs),
                startedAt: existing.startedAt || failedAt,
                updatedAt: failedAt,
                completedAt: mailAuthNeeded ? null : failedAt,
                errorCode: data.error_code || '',
                gmailAuthNeeded: Boolean(data.gmail_auth_needed),
                mailAuthNeeded,
              },
            };
          });
        } catch (_) {}
        _closeSSE(searchId);
        removeStoredSync(searchId);
        const cbs = callbacksRef.current[searchId];
        if (cbs?.onFail) cbs.onFail(errorPayload);
        delete callbacksRef.current[searchId];
      }
      // Connection errors: EventSource auto-reconnects, no action needed
    });

    es.addEventListener('timeout', () => {
      _closeSSE(searchId);
      removeStoredSync(searchId);
      updateSyncState(searchId, { syncing: false, phase: '', syncMessage: '', progress: '' });
    });
  }, [_closeSSE, getProgressFromLogs, updateSyncState]);

  /**
   * Start a sync for a given searchId.
   */
  /**
   * Connect to an already-running sync (started by backend, e.g., from chat).
   * Does NOT call /sync-replies — just connects SSE to the given sync_id.
   */
  const connectToExistingSync = useCallback((searchId, syncId, options = {}) => {
    if (!searchId || !syncId) return;
    // Don't attach duplicate EventSource listeners unless this is an auth-resume
    // handoff, where the old stream belongs to the failed pre-auth attempt.
    if (eventSourcesRef.current[searchId]) {
      if (!options.force) return;
      _closeSSE(searchId);
    }

    const currentState = syncStates[searchId] || {};
    const initialLogs = Array.isArray(options.initialLogs) && options.initialLogs.length
      ? options.initialLogs
      : (
          !options.replaceLogs && Array.isArray(currentState.logs) && currentState.logs.length
            ? currentState.logs
            : ['Sync in progress...']
        );
    const initialProgress = options.progress || getProgressFromLogs(initialLogs) || 'Sync in progress...';

    updateSyncState(searchId, {
      syncing: true,
      phase: 'syncing',
      syncMessage: '',
      logs: initialLogs,
      progress: initialProgress,
      startedAt: Date.now(),
      completedAt: null,
      errorCode: '',
      gmailAuthNeeded: false,
      mailAuthNeeded: false,
      gmailNotConnected: false,
      mailNotConnected: false,
      resumeParsing: false, resumeTotal: 0, resumeParsed: 0, resumeFailed: 0, resumeQueued: 0,
      syncResult: null, jobTitle: options.jobTitle || '',
    });
    storeActiveSync(searchId, syncId, options.jobTitle);
    _connectSSE(searchId, syncId, options);
  }, [syncStates, updateSyncState, _connectSSE, _closeSSE, getProgressFromLogs]);

  const refreshSyncState = useCallback(async (searchId) => {
    if (!searchId) return null;

    let response;
    try {
      response = await aiAutomationApi.get(
        `/email/status-refresh/${encodeURIComponent(searchId)}`
      );
    } catch (error) {
      const status = error?.status || error?.response?.status;
      const message = String(error?.message || '').toLowerCase();
      if (status !== 404 && !message.includes('not found') && !message.includes('404')) {
        throw error;
      }

      // Compatibility for a backend process that has not yet restarted with
      // the detailed refresh route. Preserve existing logs and refresh only
      // the active flag using the older endpoint.
      const legacy = await aiAutomationApi.get(
        `/email/auto-sync-status/${encodeURIComponent(searchId)}`
      );
      const legacySyncing = Boolean(legacy?.is_running);
      updateSyncState(searchId, {
        syncing: legacySyncing,
        phase: legacySyncing ? 'syncing' : undefined,
        progress: legacySyncing ? 'Automatic email sync is processing' : undefined,
      });
      return { status: 'success', legacy: true };
    }
    const snapshot = response?.email_sync || {};
    const logs = Array.isArray(snapshot.logs) ? snapshot.logs : [];
    const syncing = Boolean(snapshot.syncing);
    const syncId = snapshot.sync_id || null;
    let phase = snapshot.phase || (syncing ? 'syncing' : '');
    const completedAt = snapshot.completed_at || snapshot.last_sync || null;
    const updatedAt = snapshot.updated_at || completedAt || snapshot.started_at || null;
    const existingState = syncStates[searchId] || {};
    const incomingIsError = phase === 'error' || phase === 'failed' || snapshot.status === 'failed';
    const existingCompletedAt = existingState.completedAt ? Date.parse(existingState.completedAt) : 0;
    const incomingUpdatedAt = updatedAt ? Date.parse(updatedAt) : 0;
    if (
      incomingIsError &&
      (existingState.phase === 'complete' || existingState.completedAt) &&
      (!incomingUpdatedAt || !existingCompletedAt || incomingUpdatedAt <= existingCompletedAt)
    ) {
      return response;
    }
    if (phase === 'failed') phase = 'error';

    updateSyncState(searchId, {
      syncing,
      phase,
      logs,
      progress: snapshot.progress || getProgressFromLogs(logs),
      startedAt: snapshot.started_at || null,
      updatedAt,
      completedAt,
      lastSync: snapshot.last_sync || snapshot.result?.last_synced_at || completedAt || null,
      senderEmail: snapshot.sender_email || response?.sender_email || '',
      resumeParsing: Boolean(snapshot.resume_parsing),
      resumeTotal: Number(snapshot.resume_total || 0),
      resumeParsed: Number(snapshot.resume_parsed || snapshot.parsed_resumes || 0),
      resumeFailed: Number(snapshot.resume_failed || 0),
      resumeQueued: Number(snapshot.resume_queued || 0),
      syncResult: snapshot.result || (
        completedAt
          ? {
              new_count: Number(snapshot.total_mails || 0),
              parsed_resumes: Number(snapshot.parsed_resumes || 0),
              duplicate_resumes: Number(snapshot.duplicate_resumes || 0),
            }
          : null
      ),
      autoSync: Boolean(snapshot.auto_sync),
    });

    if (syncing && syncId) {
      storeActiveSync(searchId, syncId, '');
      _connectSSE(searchId, syncId);
    } else {
      _closeSSE(searchId);
      removeStoredSync(searchId);
    }

    return response;
  }, [_closeSSE, _connectSSE, getProgressFromLogs, syncStates, updateSyncState]);

  const startSync = useCallback(async (searchId, options = {}) => {
    if (!searchId) return { started: false };

    // Already syncing — block, unless this is an explicit auth-resume retry.
    const current = syncStates[searchId];
    if (current?.syncing && !options.force) return { started: false, alreadySyncing: true };

    updateSyncState(searchId, {
      syncing: true, phase: 'syncing', logs: ['Starting sync...'],
      progress: 'Starting sync...',
      startedAt: Date.now(),
      completedAt: null,
      resumeParsing: false, resumeTotal: 0, resumeParsed: 0, resumeFailed: 0, resumeQueued: 0,
      syncResult: null, jobTitle: options.jobTitle || '',
    });

    try {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      if (!userId) {
        updateSyncState(searchId, { syncing: false, phase: '', syncMessage: '', logs: ['User not authenticated'], completedAt: Date.now() });
        return { started: false };
      }

      const resp = await aiAutomationApi.post('/email/sync-replies', {
        user_id: String(userId),
        search_id: searchId,
        session_id: options.sessionId || options.session_id || undefined,
        cid: options.cid || (metadata?.cid ? String(metadata.cid) : undefined),
        token: localStorage.getItem('auth_token') || undefined,
      });

      // Active mail provider connection guard
      if (resp?.gmail_auth_needed || resp?.mail_auth_needed || isMailProviderAuthError(resp)) {
        updateSyncState(searchId, {
          syncing: false,
          phase: 'auth_required',
          syncMessage: '',
          logs: ['Mail authorization required. Waiting for reconnect...'],
          progress: 'Mail authorization required. Waiting for reconnect...',
          completedAt: null,
          gmailNotConnected: true, gmailAuthNeeded: Boolean(resp?.gmail_auth_needed),
          mailNotConnected: true, mailAuthNeeded: true,
        });
        if (options.onGmailNotConnected) options.onGmailNotConnected(resp);
        else if (options.onFail) options.onFail(resp);
        return { started: false, gmailBlocked: Boolean(resp?.gmail_auth_needed), mailBlocked: true };
      }

      const syncId = resp?.sync_id;
      if (syncId) {
        storeActiveSync(searchId, syncId, options.jobTitle);
        _connectSSE(searchId, syncId, options);
        return { started: true };
      } else if (resp?.reason === 'sync_already_in_progress') {
        updateSyncState(searchId, {
          syncing: true,
          phase: 'syncing',
          logs: [resp?.message || 'Sync is already processing for this session'],
          progress: resp?.message || 'Sync is already processing for this session',
          startedAt: Date.now(),
          completedAt: null,
        });
        return { started: false, alreadySyncing: true };
      } else {
        // Instant sync (no sync_id)
        updateSyncState(searchId, { syncing: false, phase: 'complete', syncMessage: '', logs: ['Sync complete'], progress: 'Sync complete', completedAt: Date.now() });
        if (options.onComplete) options.onComplete();
        return { started: true };
      }
    } catch (err) {
      console.error('[SyncContext] Sync error:', err);
      updateSyncState(searchId, { syncing: false, phase: 'error', syncMessage: '', logs: ['Sync failed'], progress: 'Sync failed', completedAt: Date.now() });
      if (options.onFail) options.onFail();
      return { started: false, error: true };
    }
  }, [syncStates, updateSyncState, _connectSSE]);

  // Reconnect stored syncs on mount
  useEffect(() => {
    mountedRef.current = true;
    const storedSyncs = getStoredActiveSyncs();
    const entries = Object.entries(storedSyncs);

    entries.forEach(async ([searchId, { syncId, jobTitle, startedAt }]) => {
      // Skip stale syncs (> 10 minutes)
      if (startedAt && (Date.now() - startedAt) > 10 * 60 * 1000) {
        removeStoredSync(searchId);
        return;
      }
      try {
        const refreshed = await aiAutomationApi.get(
          `/email/status-refresh/${encodeURIComponent(searchId)}`
        );
        if (!refreshed?.email_sync?.syncing) {
          removeStoredSync(searchId);
          updateSyncState(searchId, {
            syncing: false,
            phase: refreshed?.email_sync?.phase || 'complete',
            logs: refreshed?.email_sync?.logs || [],
            progress: refreshed?.email_sync?.progress || '',
            completedAt: refreshed?.email_sync?.completed_at || refreshed?.email_sync?.last_sync || Date.now(),
            lastSync: refreshed?.email_sync?.last_sync || null,
            syncResult: refreshed?.email_sync?.result || null,
          });
          return;
        }
      } catch (_) {
        // If refresh fails, fall back to SSE reconnect below.
      }
      // Reconnect SSE
      updateSyncState(searchId, { syncing: true, phase: 'syncing', logs: ['Reconnecting...'], progress: 'Reconnecting...', startedAt: startedAt || Date.now(), completedAt: null, jobTitle: jobTitle || '' });
      _connectSSE(searchId, syncId);
    });

    return () => {
      mountedRef.current = false;
      // Close all SSE connections
      Object.keys(eventSourcesRef.current).forEach((sid) => {
        eventSourcesRef.current[sid]?.close();
      });
      eventSourcesRef.current = {};
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SyncContext.Provider value={{ getSyncState, isAnySyncing, getActiveSyncs, startSync, connectToExistingSync, refreshSyncState, updateSyncState, currentSearchId, setCurrentSearchId }}>
      {children}
    </SyncContext.Provider>
  );
};

export default SyncContext;
